import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { fetchCheckoutStatus } from "../lib/api";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

export default function PricingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({
    phase: sessionId ? "checking" : "missing",
    data: null,
    attempts: 0,
  });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) return;

    let timer;
    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const data = await fetchCheckoutStatus(sessionId);
        if (data.payment_status === "paid" && data.status === "complete") {
          // Track conversion
          if (
            typeof window !== "undefined" &&
            typeof window.gtag === "function"
          ) {
            window.gtag("event", "purchase", {
              event_category: "pricing",
              event_label: data.plan_id,
              value: data.amount,
              currency: data.currency.toUpperCase(),
              transaction_id: sessionId,
            });
          }
          setState({ phase: "success", data, attempts: attemptsRef.current });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", data, attempts: attemptsRef.current });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", data, attempts: attemptsRef.current });
          return;
        }
        setState({ phase: "checking", data, attempts: attemptsRef.current });
        timer = setTimeout(poll, POLL_MS);
      } catch (err) {
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          // After max attempts, treat as "still processing" (webhook likely
          // hasn't fired yet) rather than a hard error.
          setState({
            phase: "timeout",
            data: null,
            attempts: attemptsRef.current,
          });
          return;
        }
        timer = setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId]);

  const renderBody = () => {
    if (state.phase === "missing") {
      return (
        <>
          <XCircle className="w-14 h-14 text-red-500 mx-auto" />
          <h1 className="mt-6 text-3xl md:text-4xl font-extrabold text-[#0A1F47]">
            Missing session
          </h1>
          <p className="mt-3 text-[#0A1F47]/70">
            No checkout session was found. Please start from the pricing page.
          </p>
          <Link to="/pricing" className="btn-navy mt-8 inline-flex">
            Back to Pricing <ArrowRight className="w-4 h-4" />
          </Link>
        </>
      );
    }
    if (state.phase === "success") {
      const d = state.data;
      return (
        <>
          <CheckCircle2
            className="w-14 h-14 text-[#16C47F] mx-auto"
            data-testid="payment-success-icon"
          />
          <h1
            data-testid="payment-success-title"
            className="mt-6 text-3xl md:text-4xl font-extrabold text-[#0A1F47]"
          >
            You're on {d.plan_name}
          </h1>
          <p className="mt-3 text-[#0A1F47]/70">
            Payment of ${d.amount.toFixed(2)} {d.currency.toUpperCase()} confirmed. A receipt has been emailed to you.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/devops-tools" className="btn-navy inline-flex">
              Open DevOps Lab <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-[#0A1F47]/20 text-[#0A1F47] font-semibold hover:bg-[#F5F7FA] transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </>
      );
    }
    if (state.phase === "expired" || state.phase === "timeout") {
      return (
        <>
          <XCircle className="w-14 h-14 text-amber-500 mx-auto" />
          <h1 className="mt-6 text-3xl md:text-4xl font-extrabold text-[#0A1F47]">
            {state.phase === "expired" ? "Checkout expired" : "Still processing"}
          </h1>
          <p className="mt-3 text-[#0A1F47]/70 max-w-lg mx-auto">
            {state.phase === "expired"
              ? "Your checkout session expired before completing payment."
              : "We haven't received a confirmation yet. If your card was charged, you'll receive a receipt shortly."}
          </p>
          <Link to="/pricing" className="btn-navy mt-8 inline-flex">
            Try again <ArrowRight className="w-4 h-4" />
          </Link>
        </>
      );
    }
    if (state.phase === "error") {
      return (
        <>
          <XCircle className="w-14 h-14 text-red-500 mx-auto" />
          <h1 className="mt-6 text-3xl md:text-4xl font-extrabold text-[#0A1F47]">
            Unable to verify
          </h1>
          <p className="mt-3 text-[#0A1F47]/70">
            We couldn't reach our payment server. Please contact support if charged.
          </p>
          <Link to="/#contact" className="btn-navy mt-8 inline-flex">
            Contact Us
          </Link>
        </>
      );
    }
    // checking
    return (
      <>
        <Loader2 className="w-14 h-14 text-[#2E6BF6] mx-auto animate-spin" />
        <h1
          data-testid="payment-checking-title"
          className="mt-6 text-3xl md:text-4xl font-extrabold text-[#0A1F47]"
        >
          Confirming your subscription…
        </h1>
        <p className="mt-3 text-[#0A1F47]/70">
          Hang tight — we're verifying your payment with Stripe.
        </p>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-[#EDF1FB]">
      <Navbar variant="solid" />
      <section className="pt-[140px] pb-24 px-6">
        <div className="max-w-2xl mx-auto text-center bg-white rounded-3xl p-10 md:p-14 border border-[#E2E8F4] shadow-sm">
          {renderBody()}
        </div>
      </section>
      <Footer />
    </div>
  );
}
